import React, { useEffect, useMemo, useState } from 'react';
import { Check, Copy, ExternalLink, Link2, LoaderCircle, Share2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { ConfirmDangerDialog } from '../settings/ConfirmDangerDialog';
import { useI18n } from '../../hooks/useI18n';
import { copyTextToClipboard } from './MessageActions';
import {
  buildShareUrl,
  createConversationShareApi,
  fetchConversationShareStatusApi,
  revokeConversationShareApi
} from '../../api/share.api';

const COPY_RESET_MS = 2500;

export const ShareConversationDialog = ({
  open,
  onClose,
  conversationId,
  title,
  firstUserMessage,
  messageCount,
  createdAt
}) => {
  const { t, formatDate, formatNumber } = useI18n();
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState('');
  const [share, setShare] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [revokedNotice, setRevokedNotice] = useState(false);
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const shareUrl = share?.token ? buildShareUrl(share.token) : share?.shareUrl || '';

  const previewExcerpt = useMemo(() => {
    const text = `${firstUserMessage || ''}`.replace(/\s+/g, ' ').trim();
    if (!text) {
      return t('shareConversationNoMessages');
    }
    return text.length > 140 ? `${text.slice(0, 140)}…` : text;
  }, [firstUserMessage, t]);

  useEffect(() => {
    if (!open || !conversationId) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setCopied(false);
    setRevokedNotice(false);
    setShare(null);

    fetchConversationShareStatusApi(conversationId)
      .then((status) => {
        if (cancelled) {
          return;
        }
        setShare(status.shared ? status : null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || t('shareConversationLoadError'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, conversationId, t]);

  useEffect(() => {
    if (!copied) {
      return undefined;
    }
    const timer = window.setTimeout(() => setCopied(false), COPY_RESET_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const handleCreate = async () => {
    if (!conversationId || creating) {
      return;
    }
    setCreating(true);
    setError('');
    try {
      const created = await createConversationShareApi(conversationId);
      setShare(created);
      setRevokedNotice(false);
    } catch (err) {
      setError(err?.message || t('shareConversationCreateError'));
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) {
      return;
    }
    try {
      await copyTextToClipboard(shareUrl);
      setCopied(true);
    } catch {
      setError(t('shareConversationCopyError'));
    }
  };

  const handleOpen = () => {
    if (!shareUrl) {
      return;
    }
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  const handleNativeShare = async () => {
    if (!shareUrl || !canNativeShare) {
      return;
    }
    try {
      await navigator.share({
        title: title || t('shareConversationTitle'),
        text: t('shareConversationAnyone'),
        url: shareUrl
      });
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setError(t('shareConversationShareError'));
      }
    }
  };

  const handleRevoke = async () => {
    if (!conversationId || !share?.shareId || revoking) {
      return;
    }
    setRevoking(true);
    setError('');
    try {
      await revokeConversationShareApi(conversationId, share.shareId);
      setShare(null);
      setConfirmRevoke(false);
      setRevokedNotice(true);
    } catch (err) {
      setError(err?.message || t('shareConversationRevokeError'));
    } finally {
      setRevoking(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={creating || revoking ? undefined : onClose}
        closeDisabled={creating || revoking}
        title={t('shareConversationTitle')}
        panelClassName="max-w-lg rounded-2xl p-4"
        contentClassName="mb-3"
      >
        <p className="mb-4 text-sm leading-6 text-muted">{t('shareConversationAnyone')}</p>

        <div className="mb-4 rounded-2xl border border-border bg-white/70 p-4 dark:bg-slate-950/40">
          <p className="text-sm font-semibold text-text">{title || t('shareConversationUntitled')}</p>
          <p className="mt-2 line-clamp-3 text-sm leading-6 text-muted">{previewExcerpt}</p>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
            <span>{t('shareConversationMessageCount', { count: formatNumber(messageCount || 0) })}</span>
            {createdAt ? <span>{formatDate(createdAt, { dateStyle: 'medium' })}</span> : null}
          </div>
        </div>

        {loading ? (
          <div className="flex h-24 items-center justify-center text-muted">
            <LoaderCircle className="animate-spin" size={20} aria-hidden />
            <span className="sr-only">{t('shareConversationLoading')}</span>
          </div>
        ) : share ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2 dark:bg-slate-950/50">
              <Link2 size={16} className="shrink-0 text-muted" aria-hidden />
              <input
                readOnly
                value={shareUrl}
                aria-label={t('shareConversationLinkLabel')}
                className="min-w-0 flex-1 bg-transparent text-sm text-text outline-none"
                onFocus={(event) => event.target.select()}
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="button" onClick={handleCopy} className="flex-1">
                {copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
                {copied ? t('shareConversationCopied') : t('shareConversationCopy')}
              </Button>
              {canNativeShare ? (
                <Button type="button" variant="secondary" onClick={handleNativeShare} className="flex-1">
                  <Share2 size={16} aria-hidden />
                  {t('shareConversationNative')}
                </Button>
              ) : null}
            </div>
            <Button type="button" variant="ghost" onClick={handleOpen} className="w-full">
              <ExternalLink size={16} aria-hidden />
              {t('shareConversationOpen')}
            </Button>
            <div className="border-t border-border pt-3">
              <button
                type="button"
                onClick={() => setConfirmRevoke(true)}
                className="text-sm font-medium text-rose-600 transition hover:text-rose-500 dark:text-rose-300"
              >
                {t('shareConversationDisable')}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {revokedNotice ? (
              <p className="rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-muted dark:bg-slate-950/40">
                {t('shareConversationRevoked')}
              </p>
            ) : null}
            <Button type="button" onClick={handleCreate} disabled={creating} className="w-full">
              {creating ? <LoaderCircle size={16} className="animate-spin" aria-hidden /> : <Link2 size={16} aria-hidden />}
              {t('shareConversationCreate')}
            </Button>
          </div>
        )}

        {error ? <p className="mt-3 text-sm text-rose-500">{error}</p> : null}
      </Modal>

      <ConfirmDangerDialog
        open={confirmRevoke}
        title={t('shareConversationDisable')}
        message={t('shareConversationDisableConfirm')}
        confirmLabel={t('shareConversationDisable')}
        loading={revoking}
        onClose={() => (revoking ? undefined : setConfirmRevoke(false))}
        onConfirm={handleRevoke}
      />
    </>
  );
};
